import React from "react";
import ConfirmHeader from "../ConfirmHeader";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, MoreHorizontal, Check, CheckCircle, X, Trash2, EyeOff, Palette, StickyNote, ArrowUpDown, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "../Modal";
import { ModalColorPicker } from "../pickers/ModalColorPicker";
import { LockBadge } from "../LockBadge";
import { now, fmt, daysInMonth } from "../../utils/dateHelpers";
import { labelColor } from "../../utils/labelColors";
import { S, CONFIRM_STACK, CONFIRM_DANGER, CONFIRM_CANCEL } from "../../theme/sharedStyles";
import { themeToneText, readableOnWhite } from "../../theme/colorEngine";
import { focusWeekRange, donutSlices, sliceNameForSession, sliceColor, slicePercent, clampSliceOpacity, customSliceHex, SLICE_OPACITY_MIN, NO_CATEGORY } from "../../utils/focusDonut";
import { load, save } from "../../utils/storage";
import { groupSessionsByItemAndDay, withSessionNote, sameSession, hasSessionNote } from "../../utils/sessionGrouping";
import { minutesBetween, withSessionTimes, withoutSession, habitLogsAfterSessionChange } from "../../utils/sessionEdit";
import { ItemDetailsModal } from "../ItemDetailsModal";
import { isDarkBgTheme } from "../../theme/colorEngine";
import { clockInInk, ciTitleBtn } from "../../theme/clockInInk";
import { usePremium } from "../../firebase/billing";

// ─── FOCUS STACKED BAR CHART ──────────────────────────────────────────────────
// ─── UNIFIED HSL COLOR SYSTEM ─────────────────────────────────────────────────
// Generates unique, evenly-spaced pastel HSL colors per label name.
// Colors are derived from a stable hash so the same label always gets the same
// color — regardless of how many other labels exist or what order they appear in.
// hashLabel, buildColorMap, labelColor live in ../../utils/labelColors.


export function FocusStackedBarChart({ focusSessions, view }) {
  const [tooltip, setTooltip] = React.useState(null);
  const MAX_MINS = 240; // 4h cap — only for weekly/monthly single-day bars
  const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const d = now();

  if (focusSessions.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--c-text-soft)", textAlign: "center", margin: "20px 0" }}>No clock-ins yet</p>;
  }

  let bars = [];
  let isYearly = view === "yearly";

  if (view === "weekly") {
    for (let i = 6; i >= 0; i--) {
      const day = new Date(d); day.setDate(d.getDate() - i);
      const dt = fmt(day);
      const daySessions = focusSessions.filter(s => s.date === dt);
      const segments = daySessions.map(s => ({ name: s.categoryLabel?.trim() || s.label || "Uncategorized", minutes: s.minutes }));
      const total = segments.reduce((a, s) => a + s.minutes, 0);
      const dayStr = `${String(day.getDate()).padStart(2,"0")} ${MONTHS_SHORT[day.getMonth()]}`;
      bars.push({ dateLabel: dayStr, segments, total, capped: total > MAX_MINS });
    }
  } else if (view === "monthly") {
    const year = d.getFullYear(), month = d.getMonth();
    const days = daysInMonth(year, month);
    for (let i = 1; i <= days; i++) {
      const dt = `${year}-${String(month+1).padStart(2,"0")}-${String(i).padStart(2,"0")}`;
      const daySessions = focusSessions.filter(s => s.date === dt);
      const segments = daySessions.map(s => ({ name: s.categoryLabel?.trim() || s.label || "Uncategorized", minutes: s.minutes }));
      const total = segments.reduce((a, s) => a + s.minutes, 0);
      bars.push({ dateLabel: String(i), segments, total, capped: total > MAX_MINS });
    }
  } else {
    // Yearly: M1–M12 relative to first ever focus session date
    const sortedDates = [...focusSessions].map(s => s.date).sort();
    const firstDate = sortedDates[0];
    const originMs = new Date(firstDate + "T00:00:00").getTime();
    const todayMs = new Date(fmt(d) + "T00:00:00").getTime();

    for (let mi = 0; mi < 12; mi++) {
      const mStartMs = originMs + mi * 30 * 86400000;
      const mEndMs = mStartMs + 29 * 86400000;
      if (mStartMs > todayMs) {
        // Future month — empty bar
        bars.push({ dateLabel: `M${mi + 1}`, segments: [], total: 0, capped: false });
        continue;
      }
      // Collect all sessions within this 30-day window
      const monthSessions = focusSessions.filter(s => {
        const ms = new Date(s.date + "T00:00:00").getTime();
        return ms >= mStartMs && ms <= Math.min(mEndMs, todayMs);
      });
      // Group by label and sum minutes
      const labelTotals = {};
      monthSessions.forEach(s => {
        const name = s.categoryLabel?.trim() || s.label || "Uncategorized";
        labelTotals[name] = (labelTotals[name] || 0) + s.minutes;
      });
      const segments = Object.entries(labelTotals).map(([name, minutes]) => ({ name, minutes }));
      const total = segments.reduce((a, s) => a + s.minutes, 0);
      bars.push({ dateLabel: `M${mi + 1}`, segments, total, capped: false });
    }
  }

  const hasData = bars.some(b => b.total > 0);
  if (!hasData) {
    const msg = view === "weekly" ? "No clock-ins this week" : view === "monthly" ? "No clock-ins this month" : "No clock-ins yet";
    return <p style={{ fontSize: 12, color: "var(--c-text-soft)", textAlign: "center", margin: "20px 0" }}>{msg}</p>;
  }

  // Collect all unique labels for consistent color mapping
  const allLabels = [];
  bars.forEach(b => b.segments.forEach(s => { if (!allLabels.includes(s.name)) allLabels.push(s.name); }));

  // Y-axis: yearly auto-scales to actual max total minutes; weekly/monthly capped at 4h
  const yMax = isYearly
    ? Math.max(...bars.map(b => b.total), 60)
    : MAX_MINS;
  // Y-axis ticks
  const yTicks = isYearly
    ? (() => {
        const step = yMax <= 120 ? 30 : yMax <= 300 ? 60 : yMax <= 600 ? 120 : yMax <= 1200 ? 240 : 480;
        const ticks = [];
        for (let t = 0; t <= yMax; t += step) ticks.push(t);
        if (ticks[ticks.length - 1] < yMax) ticks.push(Math.ceil(yMax / step) * step);
        return ticks;
      })()
    : [0, 30, 60, 90, 120, 150, 180, 210, 240];
  const yPct = (mins) => 1 - Math.min(mins, yMax) / yMax;

  const H = 180;
  const PAD_TOP = 12, PAD_BOT = 28, PAD_L = 4, PAD_R = 8;
  const chartH = H - PAD_TOP - PAD_BOT;

  const yAxisW = isYearly ? 36 : 28;

  return (
    <div style={{ width: "100%", position: "relative", userSelect: "none" }}>
      <div style={{ display: "flex", gap: 0 }}>
        {/* Y-axis labels */}
        <div style={{ width: yAxisW, minWidth: yAxisW, position: "relative", height: H }}>
          {yTicks.map(t => {
            const topPx = PAD_TOP + chartH * yPct(t);
            const label = t === 0 ? "0" : t % 60 === 0 ? `${t/60}h` : `${t}m`;
            const isMajor = isYearly ? true : t % 60 === 0;
            return (
              <div key={t} style={{ position: "absolute", top: topPx, right: 4, transform: "translateY(-50%)", fontSize: isMajor ? 9 : 7.5, fontWeight: isMajor ? 600 : 400, color: isMajor ? "#a07090" : "#c0aabb", whiteSpace: "nowrap" }}>
                {label}
              </div>
            );
          })}
        </div>

        {/* Bars area */}
        <div style={{ flex: 1, position: "relative", height: H, overflow: "hidden" }}>
          {/* Grid lines */}
          {yTicks.map(t => {
            const topPx = PAD_TOP + chartH * yPct(t);
            return (
              <div key={t} style={{ position: "absolute", left: 0, right: 0, top: topPx, height: 1, background: "var(--c-bg-active)", zIndex: 0 }} />
            );
          })}

          {/* Bars */}
          <div style={{ display: "flex", alignItems: "flex-end", height: H - PAD_BOT, paddingTop: PAD_TOP, gap: view === "yearly" ? 4 : view === "monthly" ? 2 : 6, paddingLeft: PAD_L, paddingRight: PAD_R, boxSizing: "border-box", position: "relative", zIndex: 1 }}>
            {bars.map((bar, bi) => {
              const effectiveTotal = isYearly ? bar.total : Math.min(bar.total, MAX_MINS);
              const barPxH = (effectiveTotal / yMax) * chartH;
              if (bar.total === 0) {
                return <div key={bi} style={{ flex: 1, minWidth: 0 }} />;
              }
              // For yearly: use actual minutes per label (no cap); for daily: cap at MAX_MINS
              const cappedSegs = isYearly
                ? bar.segments.map(seg => ({ ...seg, visibleMins: seg.minutes }))
                : (() => {
                    let running = 0;
                    return bar.segments.map(seg => {
                      const visible = Math.min(seg.minutes, MAX_MINS - running);
                      running += visible;
                      return { ...seg, visibleMins: visible };
                    }).filter(s => s.visibleMins > 0);
                  })();

              return (
                <div key={bi} style={{ flex: 1, minWidth: 0, height: barPxH, display: "flex", flexDirection: "column-reverse", borderRadius: "3px 3px 0 0", overflow: "hidden", cursor: "pointer", position: "relative" }}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {cappedSegs.map((seg, si) => {
                    const segH = (seg.visibleMins / yMax) * chartH;
                    const color = labelColor(seg.name, allLabels);
                    const isUnlabeled = seg.name === "Clock-in";
                    return (
                      <div key={si}
                        style={{ width: "100%", height: segH, background: color, borderTop: si < cappedSegs.length - 1 ? "1px solid rgba(255,255,255,0.4)" : "none", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0, position: "relative" }}
                        onMouseEnter={() => setTooltip({ key: `${bi}-${si}`, name: seg.name, minutes: seg.minutes })}
                      >
                        {segH > 14 && (
                          <span style={{ fontSize: Math.min(8, segH * 0.45), color: "rgba(60,30,50,0.7)", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", padding: "0 2px", maxWidth: "100%", display: "block", textAlign: "center" }}>
                            {isUnlabeled ? "" : seg.name}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {!isYearly && bar.capped && (
                    <div style={{ position: "absolute", top: 1, left: 0, right: 0, textAlign: "center", fontSize: 7, color: "var(--c-text-mid)", fontWeight: 700, zIndex: 5 }}>4h+</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div style={{ display: "flex", gap: view === "yearly" ? 4 : view === "monthly" ? 2 : 6, paddingLeft: PAD_L, paddingRight: PAD_R, boxSizing: "border-box", height: PAD_BOT }}>
            {bars.map((bar, bi) => (
              <div key={bi} style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: 8.5, color: "#b090a4", paddingTop: 3, overflow: "hidden", whiteSpace: "nowrap" }}>
                {view === "monthly" ? (bi === 0 || bi === 14 || bi === bars.length - 1 ? bar.dateLabel : "") : bar.dateLabel}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ background: "var(--c-bg-card)", border: "1px solid #f0d0e8", borderRadius: 8, padding: "6px 10px", fontSize: 11, color: "var(--c-text-mid)", boxShadow: "0 2px 10px rgba(212,120,154,0.18)", textAlign: "center", marginTop: 6 }}>
          <span style={{ fontWeight: 600 }}>{tooltip.name}</span>: {tooltip.minutes}m{isYearly ? ` (${(tooltip.minutes/60).toFixed(1)}h)` : ""}
        </div>
      )}

      {/* Legend */}
      {allLabels.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginTop: tooltip ? 4 : 10, paddingLeft: 32 }}>
          {allLabels.map(label => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "var(--c-text-mid)" }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: labelColor(label, allLabels), flexShrink: 0 }} />
              <span style={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FOCUS BULLET JOURNAL CHART (legacy — kept for reference, replaced above) ──
export function FocusBulletChart({ focusSessions, view }) {
  return <FocusStackedBarChart focusSessions={focusSessions} view={view} />;
}

// ─── FOCUS PIE CHART ──────────────────────────────────────────────────────────

// Inline note editor shown right under a logged-session row (Jade, 2026-10-11):
// tapping the row's note icon opens just this, not the full details panel.
// It does NOT focus itself: the keyboard opens only when the field is tapped
// (Jade, 2026-10-11), so opening it just to read the note doesn't pop it up.
function InlineNoteEditor({ note, onSave, onClose }) {
  const [draft, setDraft] = React.useState(note || "");
  const [saved, setSaved] = React.useState(false);
  // Save keeps the field open (Jade, 2026-10-11): the row's note icon hides it.
  // The keyboard goes down and "Saved" flashes. An emptied note removes the
  // note, and with it the row's icon, so then it closes (nothing to tap).
  const save = () => {
    if (draft.trim() !== (note || "").trim()) onSave(draft);
    if (!draft.trim()) { onClose(); return; }
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setSaved(true);
    setTimeout(() => setSaved(false), 1200);
  };
  const link = { background: "none", border: "none", padding: "4px 2px", minHeight: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 800 };
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ padding: "0 13px 12px" }}>
      <textarea value={draft} rows={2} maxLength={500} placeholder="How did it go?"
        onChange={(e) => setDraft(e.target.value)}
        style={{ width: "100%", boxSizing: "border-box", resize: "none", borderRadius: 10, border: "1.5px solid var(--c-border-mid)", background: "var(--c-bg-card)", color: "var(--c-text)", fontFamily: "inherit", fontSize: 13, fontWeight: 600, padding: "8px 10px", outline: "none" }} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 2 }}>
        <button type="button" onClick={save} style={{ ...link, color: "var(--c-text-accent)" }}>{saved ? "Saved ✓" : "Save"}</button>
      </div>
    </div>
  );
}

// One category row of the desktop Chart page's logged sessions, draggable in the
// Custom order (like the Habits tab's custom order). children gets the grip's props.
function SortableLegendRow({ id, enabled, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !enabled });
  if (!enabled) return children(null);
  return (
    <div ref={setNodeRef} style={{ position: "relative", transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 10 : "auto", opacity: isDragging ? 0.85 : 1 }}>
      {children({ ...attributes, ...listeners })}
    </div>
  );
}

export function FocusPieChart({ focusSessions, setFocusSessions = () => {}, habits = [], habitLogs = null, setHabitLogs = null, tasks = [], setTasks = () => {}, categories = [], setCategories = null, isDesktop = false, toolbarSlotNode = null, viewSlotNode = null, weekStart, bare = false, wide = false }) {
  // wide = the same page on a computer (Jade's mockup G, 2026-11-02): the period
  // dropdown and dates centred above the totals, the donut beside a logged sessions
  // list that scrolls on its own. Only with bare.
  // bare = the Clock-In Chart page, straight on the page background: black /
  // white text, a pill switch, the total inside the donut (Jade, 2026-10-24).
  const ci = bare ? clockInInk() : null;
  const bareVars = bare ? {
    "--c-text": ci.ink, "--c-text-mid": ci.ink, "--c-text-soft": ci.soft,
    "--c-bg-card": ci.glass.background, "--c-border": ci.faint, "--c-border-mid": ci.faint,
    ...(ci.dark ? { "--c-text-accent": "#ffffff" } : {}),
  } : {};
  // Settings → "Week starts on" decides the Week view's days (and the donut
  // widget's — both go through focusWeekRange). Read from storage when a caller
  // doesn't pass it.
  const weekStartDay = weekStart || load("clarity_weekStart", "sunday");
  // Custom to-dos live only in FocusTab's local state, persisted here — read
  // straight from storage rather than threading them through every parent.
  const customTodos = load("clarity_focusCustomTodos", []);
  const resolveItemRef = (kind, id) => kind === "habit" ? habits.find(h => h.id === id)
    : kind === "task" ? tasks.find(t => t.id === id)
    : kind === "custom" ? customTodos.find(c => c.id === id) : null;
  const [noteOpenFor, setNoteOpenFor] = React.useState(null); // sigOf(primary) of the row whose inline note editor is open
  const [sessionDetailsFor, setSessionDetailsFor] = React.useState(null); // logged session opened via its details panel
  // Month/Year views of the donut + logged sessions are Premium — Week stays free.
  const { isPremium } = usePremium();
  // Every logged session with no linked category, habit, or task collapses into
  // one gray "Uncategorized" slice (matches the stacked bar chart above). The
  // grouping and colours live in utils/focusDonut, shared with the donut widget.
  const sessionCatKey = (s) => sliceNameForSession(s, categories);

  // Sessions store their label as "Habit: Study" / "Task: …" / "To-do: …". The
  // kind is redundant next to a badge, so strip that prefix for display.
  const cleanSessionTitle = (s) => {
    if (s.kind && s.label) { const i = s.label.indexOf(": "); if (i >= 0) return s.label.slice(i + 2).trim() || s.label; }
    return s.label?.trim() || "Clock-in";
  };
  const sigOf = (s) => s.id || `${s.date}_${s.startedAt || s.completedAt || ""}_${s.minutes}_${s.label || ""}`;
  const fmtMins = (m) => m < 60 ? `${m}m` : `${Math.floor(m / 60)}h${m % 60 > 0 ? ` ${m % 60}m` : ""}`;
  const [pieView, setPieView] = React.useState("weekly");
  // If Premium lapses while a locked view is open, fall back to Week rather
  // than leaving a non-premium user stuck looking at a Month/Year view.
  React.useEffect(() => {
    if (!isPremium && (pieView === "monthly" || pieView === "yearly")) setPieView("weekly");
  }, [isPremium, pieView]);
  // Owned here (via its own meatballs menu) — other listeners (e.g. the main
  // Clock-In card) still pick up changes through the same custom event, since
  // a plain "storage" event never fires for changes made in the same tab.
  const [hideDuration, setHideDuration] = React.useState(() => load("clarity_focusHideDuration", false));
  React.useEffect(() => {
    const onChange = () => setHideDuration(load("clarity_focusHideDuration", false));
    window.addEventListener("clarity-hide-session-duration", onChange);
    return () => window.removeEventListener("clarity-hide-session-duration", onChange);
  }, []);
  const toggleHideDuration = () => setHideDuration(v => {
    const next = !v;
    save("clarity_focusHideDuration", next);
    window.dispatchEvent(new Event("clarity-hide-session-duration"));
    return next;
  });

  // ── Select mode — bulk-delete whole categories and/or individual logged
  //    sessions, opened from the meatballs menu above the donut. ──────────────
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [selectMode, setSelectMode] = React.useState(false);
  const [selectedCats, setSelectedCats] = React.useState(() => new Set());
  const [selectedSessions, setSelectedSessions] = React.useState(() => new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = React.useState(false);
  const exitSelectMode = () => { setSelectMode(false); setSelectedCats(new Set()); setSelectedSessions(new Set()); };
  const toggleCatSelected = (name) => setSelectedCats(prev => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; });
  const toggleSessionSelected = (sig) => setSelectedSessions(prev => { const next = new Set(prev); next.has(sig) ? next.delete(sig) : next.add(sig); return next; });
  const selectedCount = selectedCats.size + selectedSessions.size;
  const performBulkDelete = () => {
    setFocusSessions(prev => prev.filter(s => !selectedCats.has(sessionCatKey(s)) && !selectedSessions.has(sigOf(s))));
    setConfirmBulkDelete(false);
    exitSelectMode();
  };
  // Calendar-aligned navigation — each view keeps its own offset from the
  // current period so switching Week/Month/Year doesn't lose your place.
  // No bound in either direction: "<" and ">" always stay enabled so past
  // periods (with no data yet limiting how far back you can look) and
  // future ones are both reachable.
  const [pieWeekOffset, setPieWeekOffset] = React.useState(0);
  const [pieMonthOffset, setPieMonthOffset] = React.useState(0);
  const [pieYearOffset, setPieYearOffset] = React.useState(0);
  const [expandedSlice, setExpandedSlice] = React.useState(null);
  // Slice colours (Jade, 2026-10-06): "Edit slice colours" in the meatball menu
  // turns on colourEditMode ("Tap a category to edit its colour"); tapping a
  // category opens the editor with a draft { name, hex, opacity }. The draft
  // previews on the donut at once and is written to the category (sliceHex /
  // sliceOpacity — donut-only fields, see focusDonut.js) only on Save, so
  // dragging the picker isn't hundreds of saves and widget re-sends.
  const [colourEditMode, setColourEditMode] = React.useState(false);
  const [colourDraft, setColourDraft] = React.useState(null); // { name, hex, opacity }
  const openColourEditor = (name) => {
    const cat = categories.find(c => c.title === name);
    if (!cat) return;
    setColourDraft({ name, hex: customSliceHex(cat) || "", opacity: clampSliceOpacity(cat.sliceOpacity) });
  };
  // Closing the panel (or Save) only closes the panel — colour editing stays on
  // so several categories can be done in a row; the hint's Done link ends it
  // (Jade, 2026-10-06).
  const closeColourEditor = () => { setColourDraft(null); };
  const endColourEditing = () => { setColourDraft(null); setColourEditMode(false); };
  // "Save" turns into "Saved!" for a moment before the panel closes — same
  // timing as the habit form's triggerSavePop (HabitsTab, 380ms).
  const [colourSaving, setColourSaving] = React.useState(false);
  const saveColourDraftWithPop = () => {
    if (colourSaving) return;
    setColourSaving(true);
    setTimeout(() => { saveColourDraft(); setColourSaving(false); }, 380);
  };
  const saveColourDraft = () => {
    const d = colourDraft;
    closeColourEditor();
    if (!d || !setCategories) return;
    const hex = /^#[0-9a-f]{6}$/i.test(d.hex || "") ? d.hex.toLowerCase() : "";
    const opacity = Math.round(clampSliceOpacity(d.opacity) * 100) / 100;
    setCategories(prev => prev.map(c => {
      if (c.title !== d.name) return c;
      const { sliceHex: _h, sliceOpacity: _o, ...rest } = c;
      return { ...rest, ...(hex ? { sliceHex: hex } : {}), ...(opacity < 1 ? { sliceOpacity: opacity } : {}) };
    }));
  };
  const [pulseSlice, setPulseSlice] = React.useState(null);

  // Listen for session-complete bridge event from FocusTab
  React.useEffect(() => {
    const handler = (e) => {
      const { categoryLabel } = e.detail || {};
      const c = categoryLabel?.trim();
      const sliceName = (c && categories.some(cat => cat.title === c)) ? c : NO_CATEGORY;
      setPulseSlice(sliceName);
      setExpandedSlice(sliceName);
      setTimeout(() => setPulseSlice(null), 2200);
    };
    window.addEventListener("clarity:sessionComplete", handler);
    return () => window.removeEventListener("clarity:sessionComplete", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  // ── Filter sessions by view — calendar-aligned periods, steppable via
  //    pieWeekOffset/pieMonthOffset/pieYearOffset instead of a rolling window,
  //    so past weeks/months/years can be browsed, not just "last 7/30 days". ──
  const fmt2 = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  const MONTHS_L = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const getWeekRange = (offset) => focusWeekRange(weekStartDay, offset);
  const getMonthRange = (offset) => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const end = new Date(today.getFullYear(), today.getMonth() + offset + 1, 0);
    return { start, end };
  };
  const getYearRange = (offset) => {
    const today = new Date();
    const y = today.getFullYear() + offset;
    return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
  };
  const filterSessions = (all, view) => {
    if (view === "weekly") {
      const { start, end } = getWeekRange(pieWeekOffset);
      return all.filter(s => s.date >= fmt2(start) && s.date <= fmt2(end));
    } else if (view === "monthly") {
      const { start, end } = getMonthRange(pieMonthOffset);
      return all.filter(s => s.date >= fmt2(start) && s.date <= fmt2(end));
    }
    const { start, end } = getYearRange(pieYearOffset);
    return all.filter(s => s.date >= fmt2(start) && s.date <= fmt2(end));
  };

  const sessions = filterSessions(focusSessions, pieView);
  const fmtShortDate = (d) => `${MONTHS_L[d.getMonth()].slice(0,3)} ${d.getDate()}`;
  const periodLabel = (() => {
    if (pieView === "weekly") {
      const { start, end } = getWeekRange(pieWeekOffset);
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${fmtShortDate(start)} - ${end.getDate()}, ${end.getFullYear()}`;
      }
      if (start.getFullYear() === end.getFullYear()) return `${fmtShortDate(start)} - ${fmtShortDate(end)}, ${end.getFullYear()}`;
      return `${fmtShortDate(start)}, ${start.getFullYear()} - ${fmtShortDate(end)}, ${end.getFullYear()}`;
    } else if (pieView === "monthly") {
      const { start } = getMonthRange(pieMonthOffset);
      return `${MONTHS_L[start.getMonth()]} ${start.getFullYear()}`;
    }
    return `${getYearRange(pieYearOffset).start.getFullYear()}`;
  })();
  const viewLabel = `for ${periodLabel}`;
  const setCurrentPieOffset = pieView === "weekly" ? setPieWeekOffset : pieView === "monthly" ? setPieMonthOffset : setPieYearOffset;

  // ── Group & sort ─────────────────────────────────────────────────────────────
  // One slice per category, biggest first — every category, no cap and no
  // "Other" (Jade, 2026-10-03). Colours: utils/focusDonut's sliceColor, the
  // category's own Spaces colour with a small saturation boost.
  const donut = donutSlices(sessions, categories);
  const totalMins = donut.reduce((a, d) => a + d.mins, 0);
  const slices = donut.map(d => [d.name, d.mins]);
  // Desktop Chart page: the logged sessions' category rows can be sorted (Jade,
  // 2026-11-02). The donut keeps its own order. Remembered per device (no clarity_
  // prefix, so it isn't synced).
  const [rowSort, setRowSort] = React.useState(() => { try { return localStorage.getItem("clarityClockInRowSort") || "most"; } catch { return "most"; } });
  const pickRowSort = (v) => { setRowSort(v); try { localStorage.setItem("clarityClockInRowSort", v); } catch {} };
  // Custom = dragged into place (Jade, 2026-11-02): a list of category names; ones not
  // in it (new categories) follow in Most time order. Per device, like the sort.
  const [rowOrder, setRowOrder] = React.useState(() => { try { return JSON.parse(localStorage.getItem("clarityClockInRowOrder") || "[]"); } catch { return []; } });
  const saveRowOrder = (names) => { setRowOrder(names); try { localStorage.setItem("clarityClockInRowOrder", JSON.stringify(names)); } catch {} };
  const sortMode = rowSort === "least" || rowSort === "custom" ? rowSort : "most"; // older "az" / "za" picks read as Most time
  const legendSlices = !wide || sortMode === "most" ? slices
    : sortMode === "least" ? [...slices].sort((a, b) => (a[1] - b[1]) || a[0].localeCompare(b[0]))
    : (() => { const at = new Map(rowOrder.map((n, i) => [n, i])); return [...slices].sort((a, b) => (at.has(a[0]) ? at.get(a[0]) : 1e6) - (at.has(b[0]) ? at.get(b[0]) : 1e6)); })();
  const customRows = wide && sortMode === "custom";
  const rowDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const onRowDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const names = legendSlices.map(([n]) => n);
    const from = names.indexOf(active.id), to = names.indexOf(over.id);
    if (from === -1 || to === -1) return;
    // Keep the places of categories not shown this period.
    const moved = arrayMove(names, from, to);
    saveRowOrder([...moved, ...rowOrder.filter(n => !moved.includes(n))]);
  };
  const [sortMenu, setSortMenu] = React.useState(null); // { top, left } while open
  const sessionCounts = {};
  const sliceColors = {};
  donut.forEach(d => { sessionCounts[d.name] = d.count; sliceColors[d.name] = d.color; });
  const getSliceColor = (name) => {
    if (colourDraft && colourDraft.name === name) {
      return sliceColor(name, categories.map(c => c.title === name ? { ...c, sliceHex: colourDraft.hex, sliceOpacity: colourDraft.opacity } : c));
    }
    return sliceColors[name] || sliceColor(name, categories);
  };

  // ── Geometry — unified for all screen sizes ───────────────────────────────────
  // ri pulled in a bit from 48 so the band reads a touch thicker/bolder — same
  // outer radius, same slice colors, just a bit more colored surface visible.
  const R = 80, ri = 43;
  const GAP = 0.022;
  const VW = 220, VH = 220;
  const cx = VW / 2, cy = VH / 2;
  // The donut's hole as a % of the SVG's own box — used to size the HTML
  // gradient-fill div sitting behind it (see DonutHoleGradient below).
  const HOLE_PCT = `${(((ri - 0.5) * 2) / VW) * 100}%`;

  const makeArc = (startAngle, endAngle) => {
    const s = startAngle + GAP / 2, e = endAngle - GAP / 2;
    if (e <= s) return null;
    const ox1 = cx + R * Math.cos(s), oy1 = cy + R * Math.sin(s);
    const ox2 = cx + R * Math.cos(e), oy2 = cy + R * Math.sin(e);
    const ix1 = cx + ri * Math.cos(e), iy1 = cy + ri * Math.sin(e);
    const ix2 = cx + ri * Math.cos(s), iy2 = cy + ri * Math.sin(s);
    const large = (e - s) > Math.PI ? 1 : 0;
    return `M${ox1},${oy1} A${R},${R} 0 ${large} 1 ${ox2},${oy2} L${ix1},${iy1} A${ri},${ri} 0 ${large} 0 ${ix2},${iy2} Z`;
  };

  let cumAngle = -Math.PI / 2;
  const paths = slices.map(([name, mins]) => {
    const pct = totalMins > 0 ? mins / totalMins : 0;
    const color = getSliceColor(name);
    const spanAngle = pct * 2 * Math.PI;
    const startAngle = cumAngle;
    const endAngle = cumAngle + spanAngle;
    const midAngle = (startAngle + endAngle) / 2;
    cumAngle = endAngle;
    if (slices.length === 1) return { isRing: true, color, name, mins, pct, midAngle };
    return { d: makeArc(startAngle, endAngle), color, name, mins, pct, midAngle };
  });

  // The donut's center used to hold a decorative animation (spinning globe,
  // then gradient blobs); now it's just a plain "hole" that reveals the
  // app's own theme gradient underneath (see DonutHoleGradient below) — no
  // SVG content of its own needed here at all.

  // ── View toggle ───────────────────────────────────────────────────────────────
  // Chart page with a top-bar slot: a "Week ▾" dropdown beside the menu button
  // instead of the pill switch (Jade, 2026-10-24), like the Planner list's
  // "All Items ▾". Its menu is portalled to <body> under the button.
  const [viewMenu, setViewMenu] = React.useState(null); // { top, left } while open
  const viewName = (v) => v === "weekly" ? "Week" : v === "monthly" ? "Month" : "Year";
  const viewDropdown = bare && viewSlotNode ? (
    <>
      <button onClick={(e) => {
        if (viewMenu) { setViewMenu(null); return; }
        const r = e.currentTarget.getBoundingClientRect();
        setViewMenu({ top: r.bottom + 6, left: r.left });
      }} aria-label={`${viewName(pieView)}. Change period`} style={wide ? { ...ciTitleBtn(ci), marginRight: 0, fontSize: 20 } : ciTitleBtn(ci)}>
        {viewName(pieView)}
        {/* The same solid ▼ as the Planner list's "All Items ▼" (Jade). */}
        <span style={{ fontSize: 11, opacity: 0.85 }}>▼</span>
      </button>
      {viewMenu && createPortal(
        <>
          <div onClick={() => setViewMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
          <div style={{ position: "fixed", top: viewMenu.top, left: viewMenu.left, zIndex: 9999, minWidth: 170, overflow: "hidden", borderRadius: 14, background: "var(--c-bg-card)", border: "1.5px solid var(--c-border)", boxShadow: "0 4px 20px var(--c-shadow)", fontFamily: "'Quicksand', sans-serif" }}>
            {["weekly", "monthly", "yearly"].map((v, i) => {
              const locked = !isPremium && (v === "monthly" || v === "yearly");
              const on = pieView === v;
              return (
                <button key={v} onClick={() => { if (locked) return; setPieView(v); setExpandedSlice(null); setViewMenu(null); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderTop: i ? "1px solid var(--c-border-light)" : "none", background: "transparent", color: "var(--c-text)", fontSize: 13.5, fontWeight: on ? 800 : 700, cursor: locked ? "default" : "pointer", textAlign: "left", fontFamily: "inherit" }}>
                  <span style={{ flex: 1 }}>{viewName(v)}</span>
                  {locked ? <LockBadge size={16} /> : on ? <Check size={15} strokeWidth={3} color="var(--c-primary)" /> : <span style={{ width: 15 }} />}
                </button>
              );
            })}
          </div>
        </>, document.body)}
    </>
  ) : null;
  const viewToggle = viewDropdown ? (wide ? null : createPortal(viewDropdown, viewSlotNode)) : (
    <div style={bare
      ? { display: "flex", gap: 4, background: ci.track, borderRadius: 99, padding: 4, marginBottom: 10 }
      : { display: "flex", gap: 10, background: "transparent", borderRadius: 14, padding: 0, marginBottom: 10 }}>
      {["weekly", "monthly", "yearly"].map(v => {
        const locked = !isPremium && (v === "monthly" || v === "yearly");
        return (
        <button key={v} onClick={() => {
          // Locked tabs are just a locked indicator now — not tappable into
          // the Premium page. The Settings panel's Upgrade button is the
          // one path there.
          if (locked) return;
          setPieView(v); setExpandedSlice(null);
        }} style={bare ? {
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          fontSize: isDesktop ? 16 : 15, fontWeight: pieView === v ? 800 : 600, padding: "7px 6px", borderRadius: 99, border: "none",
          cursor: locked ? "default" : "pointer", fontFamily: "inherit", minHeight: 0,
          background: pieView === v ? ci.pillOn : "transparent",
          color: ci.ink, opacity: pieView === v ? 1 : 0.65,
          transition: "background 0.18s ease, opacity 0.18s ease",
        } : {
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
          fontSize: isDesktop ? 16 : 15, fontWeight: 700, padding: "6px 6px", borderRadius: 10, border: "none",
          cursor: locked ? "default" : "pointer", fontFamily: "inherit",
          background: pieView === v ? "var(--c-bg-card)" : "transparent",
          color: pieView === v ? "var(--c-text-accent)" : "var(--c-text-soft)",
          boxShadow: pieView === v ? "0 1px 4px var(--c-shadow)" : "none",
          transition: "all 0.18s ease",
        }}>
          {v === "weekly" ? "Week" : v === "monthly" ? "Month" : "Year"}
          {locked && <LockBadge size={16} />}
        </button>
        );
      })}
    </div>
  );

  // ── Period navigation — "<" / ">" always enabled so any past or future
  //    week/month/year is reachable, not just the current one. ─────────────────
  const periodNavBtnStyle = {
    background: "none", border: "none", borderRadius: 10,
    width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", color: bare ? ci.ink : "var(--c-text-accent)", flexShrink: 0,
  };
  const periodNav = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: wide ? 0 : 14 }}>
      <button onClick={() => setCurrentPieOffset(o => o - 1)} style={periodNavBtnStyle} title="Previous period"><ChevronLeft size={16} /></button>
      <span style={{ fontSize: wide ? 16 : 13, fontWeight: bare ? 800 : 700, color: bare ? ci.ink : "var(--c-text-accent)", minWidth: 140, textAlign: "center" }}>{periodLabel}</span>
      <button onClick={() => setCurrentPieOffset(o => o + 1)} style={periodNavBtnStyle} title="Next period"><ChevronRight size={16} /></button>
    </div>
  );

  // Desktop: "Week ▼ | ‹ dates ›" in one centred row above the totals.
  const wideHeader = wide && viewDropdown ? createPortal(
    <>
      {viewDropdown}
      <span aria-hidden="true" style={{ width: 1, height: 22, background: ci.faint }} />
      {periodNav}
    </>, viewSlotNode) : null;
  const navBlock = wideHeader || periodNav;

  // A plain white circle behind the SVG donut — the donut's transparent
  // hole just reveals it, giving a clean white center instead of a themed
  // gradient.
  const DonutHoleGradient = () => (
    <div style={{
      position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
      width: HOLE_PCT, height: HOLE_PCT, borderRadius: "50%",
      background: bare ? "transparent" : "#ffffff",
    }} />
  );
  // A white disc behind the donut (Chart page only, every background): straight on the
  // page the slice colours washed out (Jade, 2026-10-24). The total inside it is
  // therefore always dark text, even on dark backgrounds.
  const donutSurface = bare && (
    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
      width: `${((R + 6) * 2 / VW) * 100}%`, height: `${((R + 6) * 2 / VH) * 100}%`,
      borderRadius: "50%", background: "#ffffff", boxShadow: "0 6px 22px rgba(0,0,0,0.07)" }} />
  );
  // ── Empty state ───────────────────────────────────────────────────────────────
  // Also renders the Premium gate: this early return skipped it entirely
  // before, so a free user with zero logged sessions who tapped the locked
  // Month/Year tab got no modal at all — the tap looked like it did nothing.
  if (sessions.length === 0) {
    return (
      <div style={{ width: "100%", ...bareVars }}>
        {viewToggle}
        {navBlock}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "10px 0" }}>
          <div data-tour="clockin-donut" style={{ position: "relative", width: wide ? 300 : "55%", margin: "0 auto" }}>
            {donutSurface}
            <DonutHoleGradient />
            <svg className="focus-donut-svg" viewBox={`0 0 ${VW} ${VH}`} style={{ display: "block", width: "100%", position: "relative" }}>
              <circle cx={cx} cy={cy} r={R} fill={bare ? "rgba(0,0,0,0.08)" : "#f5eaf0"} />
            </svg>
          </div>
          <p style={{ fontSize: 12, color: "var(--c-text-soft)", margin: 0 }}>No clock-ins {viewLabel}</p>
        </div>
      </div>
    );
  }

  // ── Donut SVG — slices are tappable, selected slice is highlighted ─────────────
  // Flat solid fills — no per-slice radial gradient, which used to read as a
  // subtle grayish bevel/blend between wedges.
  const donutSvg = (
    <div data-tour="clockin-donut" style={{ position: "relative", width: wide ? "100%" : "65%", maxWidth: wide ? 300 : 240, margin: "0 auto" }}>
      {donutSurface}
      <DonutHoleGradient />
      <svg className="focus-donut-svg" viewBox={`0 0 ${VW} ${VH}`} style={{ display: "block", width: "100%", position: "relative" }}
        preserveAspectRatio="xMidYMid meet">
        <defs>
          <style>{`
            @keyframes slicePulse {
              0%   { filter: brightness(1)    drop-shadow(0 0 0px transparent); }
              40%  { filter: brightness(1.28) drop-shadow(0 0 8px rgba(255,255,255,0.85)); }
              100% { filter: brightness(1)    drop-shadow(0 0 0px transparent); }
            }
            .clarity-slice-pulse { animation: slicePulse 0.7s ease-in-out 3; }
          `}</style>
        </defs>
        {paths.map((p, i) => {
          const isSelected = expandedSlice === p.name;
          const isPulsing  = pulseSlice === p.name;
          return p.isRing
            ? <circle key={i} cx={cx} cy={cy} r={R} fill={p.color}
                onClick={() => setExpandedSlice(isSelected ? null : p.name)}
                className={isPulsing ? "clarity-slice-pulse" : ""}
                style={{ cursor: "pointer" }} />
            : p.d ? <path key={i} d={p.d} fill={p.color}
                onClick={() => setExpandedSlice(isSelected ? null : p.name)}
                className={isPulsing ? "clarity-slice-pulse" : ""}
                style={{ cursor: "pointer", transition: "opacity 0.15s", opacity: expandedSlice && !isSelected ? 0.45 : 1 }}
              /> : null;
        })}
      </svg>
    </div>
  );

  // ── Legend — logged sessions grouped by category, inside one scrollable
  //    container so a long history never stretches the whole card/page. ──────────
  const legend = (
    <div className="focus-legend-scroll" data-tour="clockin-logged-sessions" style={{ marginTop: wide ? 0 : 14, display: "flex", flexDirection: "column", gap: 6,
      // Desktop: the list fills the screen's height and scrolls on its own; the page itself
      // doesn't scroll (Jade, 2026-11-02). 360px = the top bar, dates, totals and label above it.
      maxHeight: wide ? "max(220px, calc(100vh - var(--sat, 0px) - 360px))" : 360, overflowY: "auto", paddingRight: wide ? 12 : 2, paddingBottom: wide ? 24 : 0 }}>
      <DndContext sensors={rowDndSensors} collisionDetection={closestCenter} onDragEnd={onRowDragEnd}>
      <SortableContext items={legendSlices.map(([n]) => n)} strategy={verticalListSortingStrategy}>
      {legendSlices.map(([name, mins], i) => {
        const color = getSliceColor(name);
        // Category colors are often light pastels, which read poorly as small
        // text — darken (same hue) just for the total/percentage labels so
        // they stay legible without changing the category's actual color.
        const vividColor = bare ? ci.ink : readableOnWhite(color);
        const pct = slicePercent(mins, totalMins);
        const count = sessionCounts[name] || 0;
        const isExpanded = expandedSlice === name;

        const categorySessions = sessions
          .filter(s => sessionCatKey(s) === name)
          .sort((a, b) => (b.completedAt || b.startedAt || b.date || "").localeCompare(a.completedAt || a.startedAt || a.date || ""));
        // Same item logged more than once on the same calendar day collapses
        // into a single row here, with its minutes summed — a different day
        // still gets its own separate row.
        const sessionGroups = groupSessionsByItemAndDay(categorySessions)
          .sort((a, b) => (b.representative.completedAt || b.representative.startedAt || b.representative.date || "").localeCompare(a.representative.completedAt || a.representative.startedAt || a.representative.date || ""));
        const fmtDate = (s) => {
          const d = s.completedAt || s.startedAt;
          if (d) {
            const dt = new Date(d);
            return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: dt.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
          }
          if (s.date) {
            const [y, mo, dy] = s.date.split("-");
            const dt = new Date(+y, +mo - 1, +dy);
            return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: dt.getFullYear() !== new Date().getFullYear() ? "numeric" : undefined });
          }
          return "";
        };

        return (
          <SortableLegendRow key={name} id={name} enabled={customRows}>{(grip) => (
          <div style={{
            borderRadius: isExpanded ? "14px 14px 0 0" : 14,
            overflow: "visible",
          }}>
            {/* Category row — always visible. Just the color dot carries the
                category's color now; no tinted background or border. */}
            <div
              onClick={() => selectMode ? toggleCatSelected(name) : colourEditMode ? (name !== NO_CATEGORY && openColourEditor(name)) : setExpandedSlice(isExpanded ? null : name)}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px",
                cursor: colourEditMode && name === NO_CATEGORY ? "default" : "pointer",
                transition: "all 0.15s ease",
                // Uncategorized has no category to store a colour on, so it can't be edited.
                opacity: colourEditMode && !selectMode && name === NO_CATEGORY ? 0.45 : 1,
              }}
            >
              {/* Select-mode checkbox */}
              {selectMode && (
                <div style={{ width: 17, height: 17, borderRadius: 5, border: `1.5px solid ${selectedCats.has(name) ? "var(--c-primary)" : "var(--c-border-mid)"}`, background: selectedCats.has(name) ? "var(--c-primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {selectedCats.has(name) && <Check size={11} color="#fff" />}
                </div>
              )}

              {/* Custom order: drag by the grip (Jade, 2026-11-02). */}
              {grip && (
                <span {...grip} onClick={e => e.stopPropagation()} title="Drag to reorder"
                  style={{ display: "flex", margin: "0 -4px 0 -6px", cursor: "grab", touchAction: "none", color: "var(--c-text-soft)", flexShrink: 0 }}>
                  <GripVertical size={15} />
                </span>
              )}
              {/* Color dot */}
              <div style={{ width: 11, height: 11, borderRadius: "50%", background: color, flexShrink: 0, boxShadow: `0 0 0 2px ${color}33` }} />

              {/* Name — tap the category to see all its logged sessions. */}
              <div style={{ flex: 1, overflow: "hidden" }}>
                <p style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {name}
                </p>
              </div>

              {/* Total time */}
              {!hideDuration && (
                <span style={{ fontSize: 13, fontWeight: 700, color: vividColor, flexShrink: 0 }}>
                  {fmtMins(mins)}
                </span>
              )}

              {/* Pct badge */}
              <span style={{
                fontSize: 11, fontWeight: 700, color: vividColor,
                background: bare ? "transparent" : `${color}22`, borderRadius: 8, padding: "2px 7px",
                border: bare ? `1px solid ${ci.faint}` : `1px solid ${color}44`, flexShrink: 0,
              }}>
                {pct}%
              </span>

              {/* Chevron — its own tap target so sessions stay reachable (to pick
                  individual ones) even while the row itself toggles selection. */}
              <span onClick={e => { if (selectMode) { e.stopPropagation(); setExpandedSlice(isExpanded ? null : name); } }} style={{
                fontSize: 11, color: "var(--c-text-soft)", flexShrink: 0,
                transform: isExpanded ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
                padding: selectMode ? 4 : 0, margin: selectMode ? -4 : 0,
              }}>▾</span>
            </div>

            {/* Scrollable drawer — opens below the category row. No tint/border on
                either the wrapper or the individual session rows anymore. */}
            {isExpanded && (
              <div style={{ padding: "6px 2px 4px" }}>
                <div style={{
                  display: "flex", flexDirection: "column", gap: 0,
                  overflow: "hidden",
                  maxHeight: 246, overflowY: "auto",   // 6 rows (each row is ~41px) before scrolling
                }}>
                  {sessionGroups.length === 0 ? (
                    <p style={{ fontSize: 11, color: "var(--c-text-soft)", padding: "10px 12px", margin: 0 }}>No clock-ins {viewLabel}</p>
                  ) : sessionGroups.map((group, j) => {
                    const primary = group.representative;
                    const title = cleanSessionTitle(primary);
                    const dateStr = fmtDate(primary);
                    const groupSigs = group.sessions.map(sigOf);
                    const groupSelected = groupSigs.every(gs => selectedSessions.has(gs));
                    const toggleGroupSelected = () => setSelectedSessions(prev => {
                      const next = new Set(prev);
                      if (groupSelected) groupSigs.forEach(gs => next.delete(gs));
                      else groupSigs.forEach(gs => next.add(gs));
                      return next;
                    });
                    return (
                      <div key={j}>
                        {/* Every logged session — linked to a live habit/task/to-do, to
                            one that's since been deleted, or to nothing at all
                            (Uncategorized) — opens the same shared details panel below,
                            which adapts what it shows based on what's actually available. */}
                        <div onClick={() => {
                            if (selectMode) { toggleGroupSelected(); return; }
                            setSessionDetailsFor(primary);
                          }}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 13px", cursor: "pointer" }}>
                          {selectMode && (
                            <div style={{ width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${groupSelected || selectedCats.has(name) ? "var(--c-primary)" : "var(--c-border-mid)"}`, background: groupSelected || selectedCats.has(name) ? "var(--c-primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {(groupSelected || selectedCats.has(name)) && <Check size={10} color="#fff" />}
                            </div>
                          )}
                          <span style={{ fontSize: 13, color: bare ? ci.ink : "#4a3050", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {title}
                          </span>
                          {/* Has a note — so it's findable without opening every session. */}
                          {group.sessions.some(hasSessionNote) && (
                            <button type="button" title="Edit note" aria-label="Edit note"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (selectMode) { toggleGroupSelected(); return; }
                                setNoteOpenFor(prev => prev === sigOf(primary) ? null : sigOf(primary));
                              }}
                              style={{ display: "inline-flex", alignItems: "center", background: "none", border: "none", padding: 4, margin: -4, minHeight: 0, cursor: "pointer", color: "var(--c-text-soft)", flexShrink: 0 }}><StickyNote size={13} /></button>
                          )}
                          {dateStr && (
                            <span style={{ fontSize: 11, color: "var(--c-text-soft)", flexShrink: 0, whiteSpace: "nowrap" }}>{dateStr}</span>
                          )}
                          {!hideDuration && (
                            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--c-text)", flexShrink: 0, minWidth: 34, textAlign: "right" }}>
                              {fmtMins(group.totalMinutes)}
                            </span>
                          )}
                        </div>
                        {noteOpenFor === sigOf(primary) && !selectMode && (
                          <InlineNoteEditor note={(group.sessions.find(hasSessionNote) || {}).note || ""}
                            onSave={(text) => setFocusSessions(p => withSessionNote(p, group.sessions.find(hasSessionNote) || primary, text))}
                            onClose={() => setNoteOpenFor(null)} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Footer total */}
                <div style={{ borderTop: `1px solid ${color}22`, marginTop: 8, paddingTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 11, color: "var(--c-text-soft)", fontWeight: 600 }}>
                    {count} {count === 1 ? "session" : "sessions"}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: bare ? ci.ink : themeToneText(color) }}>
                    {hideDuration ? `${pct}%` : `${fmtMins(mins)} · ${pct}%`}
                  </span>
                </div>
              </div>
            )}
          </div>
          )}</SortableLegendRow>
        );
      })}
      </SortableContext>
      </DndContext>
    </div>
  );

  const allCatNames = slices.map(([name]) => name);
  const allCatsSelected = allCatNames.length > 0 && allCatNames.every(n => selectedCats.has(n));
  const toggleSelectAll = () => {
    if (allCatsSelected) { setSelectedCats(new Set()); setSelectedSessions(new Set()); }
    else { setSelectedCats(new Set(allCatNames)); setSelectedSessions(new Set()); }
  };

  // Select-mode bar — stays inline above the donut. All four pieces (checkbox,
  // "Select all" label, count, trash, close) share the same fixed height so
  // the icon buttons can't drift off the checkbox/label's baseline.
  const ROW_H = 26;
  const selectToolbar = selectMode ? (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, background: "var(--c-bg-card)", border: "1px solid var(--c-border)", borderRadius: 12, padding: "7px 10px" }}>
      <button onClick={toggleSelectAll} style={{ display: "flex", alignItems: "center", gap: 6, height: ROW_H, background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" }}>
        <div style={{ width: 17, height: 17, borderRadius: 5, border: `1.5px solid ${allCatsSelected ? "var(--c-primary)" : "var(--c-border-mid)"}`, background: allCatsSelected ? "var(--c-primary)" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {allCatsSelected && <Check size={11} color="#fff" />}
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text-mid)" }}>Select all</span>
      </button>
      <span style={{ flex: 1, height: ROW_H, display: "flex", alignItems: "center", justifyContent: "flex-end", fontSize: 11.5, color: "var(--c-text-soft)" }}>{selectedCount > 0 ? `${selectedCount} selected` : ""}</span>
      <button onClick={() => selectedCount > 0 && setConfirmBulkDelete(true)} disabled={selectedCount === 0}
        style={{ width: ROW_H, height: ROW_H, background: "none", border: "none", cursor: selectedCount > 0 ? "pointer" : "default", color: selectedCount > 0 ? "#c0607a" : "var(--c-border-mid)", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Trash2 size={16} />
      </button>
      <button onClick={exitSelectMode} style={{ width: ROW_H, height: ROW_H, background: "none", border: "none", cursor: "pointer", color: "var(--c-text-soft)", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <X size={16} />
      </button>
    </div>
  ) : null;

  // Meatballs trigger + dropdown — rendered outside the card, portaled beside
  // the "Back to Clock-In" button via toolbarSlotNode. Falls back to an
  // inline top-right button when no slot is supplied.
  const meatballMenu = !selectMode ? (
    <div style={{ display: "flex", position: "relative" }}>
      <button onClick={() => setMenuOpen(v => !v)} title="More options" aria-label="More options"
        style={bare
          ? { width: 40, height: 40, minHeight: 0, minWidth: 0, background: "none", border: "none", cursor: "pointer", color: ci.ink, padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }
          : { background: "none", border: "none", cursor: "pointer", color: isDarkBgTheme() ? "#ffffff" : "#000000", padding: 6, display: "flex" }}>
        {/* Black, or white on dark backgrounds — never the accent (Jade, 2026-10-11). */}
        <MoreHorizontal size={bare ? 22 : 18} strokeWidth={bare ? 2.6 : 2} />
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200 }} />
          <div style={{ position: "absolute", top: "calc(100% - 9px)", right: 0, background: "var(--c-bg-card)", borderRadius: 14, boxShadow: "0 4px 20px var(--c-shadow)", border: "1.5px solid var(--c-border)", overflow: "hidden", zIndex: 201, minWidth: 190, fontFamily: "inherit" }}>
            <button onClick={() => { setSelectMode(true); setMenuOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              <Check size={14} /> Select
            </button>
            <button onClick={() => { toggleHideDuration(); setMenuOpen(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              <EyeOff size={14} /> {hideDuration ? "Show minutes duration" : "Hide minutes duration"}
            </button>
            {setCategories && (
              <button onClick={() => { setColourEditMode(true); setColourDraft(null); setExpandedSlice(null); setMenuOpen(false); }}
                style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderTop: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                <Palette size={14} /> Edit slice colours
              </button>
            )}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div style={{ width: "100%", ...bareVars }}>
      {toolbarSlotNode ? (meatballMenu && createPortal(meatballMenu, toolbarSlotNode)) : meatballMenu}
      {selectToolbar}
      {viewToggle}
      {navBlock}
      {wide ? (
        <div style={{ display: "flex", gap: 56, alignItems: "flex-start", justifyContent: "center" }}>
          <div style={{ width: 300, flexShrink: 0 }}>{donutSvg}</div>
          <div style={{ flex: "0 1 500px", minWidth: 0 }}>
            {colourEditMode && !selectMode && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 8px", padding: "0 12px" }}>
                <Palette size={16} color={ci.ink} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, fontWeight: 700, color: ci.ink, lineHeight: 1.4 }}>Tap a category to edit its colour</span>
                <button onClick={endColourEditing}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0 2px", marginLeft: 4, minHeight: 0, minWidth: 0, color: "var(--c-text-accent)", fontSize: 12.5, fontWeight: 800, fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, flexShrink: 0 }}>
                  Done
                </button>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 12px 6px" }}>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: ci.soft }}>LOGGED SESSIONS</span>
              <button type="button" title="Sort" aria-label="Sort categories" onClick={(e) => {
                  if (sortMenu) { setSortMenu(null); return; }
                  const r = e.currentTarget.getBoundingClientRect();
                  setSortMenu({ top: r.bottom + 4, left: r.left });
                }}
                style={{ width: 30, height: 30, minHeight: 0, minWidth: 0, padding: 0, border: "none", borderRadius: 8, background: sortMenu ? ci.track : "transparent", color: ci.ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ArrowUpDown size={15} strokeWidth={2.4} />
              </button>
            </div>
            {sortMenu && createPortal(
              <>
                <div onClick={() => setSortMenu(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
                <div style={{ position: "fixed", top: sortMenu.top, left: sortMenu.left, zIndex: 9999, minWidth: 230, overflow: "hidden", borderRadius: 14, background: "var(--c-bg-card)", border: "1.5px solid var(--c-border)", boxShadow: "0 4px 20px var(--c-shadow)", fontFamily: "'Quicksand', sans-serif" }}>
                  {[["most", "Most time"], ["least", "Least time"], ["custom", "Custom (drag to reorder)"]].map(([v, label], i) => (
                    <button key={v} onClick={() => { pickRowSort(v); setSortMenu(null); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderTop: i ? "1px solid var(--c-border-light)" : "none", background: "transparent", color: "var(--c-text)", fontSize: 13.5, fontWeight: sortMode === v ? 800 : 700, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                      <span style={{ flex: 1 }}>{label}</span>
                      {sortMode === v ? <Check size={15} strokeWidth={3} color="var(--c-primary)" /> : <span style={{ width: 15 }} />}
                    </button>
                  ))}
                </div>
              </>, document.body)}
            {legend}
          </div>
        </div>
      ) : donutSvg}
      {!wide && colourEditMode && !selectMode && (
        // Plain centred text, no border or background (Jade, 2026-10-06). The Done link ends the
        // mode (so does leaving the page); Save / closing the panel keeps it on for the next category.
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, margin: "4px 0 8px", padding: "6px 12px" }}>
          <Palette size={16} color={bare ? ci.ink : "#1a1a1a"} style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: bare ? ci.ink : "#1a1a1a", lineHeight: 1.4, textAlign: "center" }}>Tap a category to edit its colour</span>
          {/* Done ends colour editing (Jade, 2026-10-06): plain underlined text in the theme accent. */}
          <button onClick={endColourEditing}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: "0 2px", marginLeft: 4, minHeight: 0, minWidth: 0, color: "var(--c-text-accent)", fontSize: 12.5, fontWeight: 800, fontFamily: "inherit", textDecoration: "underline", textUnderlineOffset: 3, flexShrink: 0 }}>
            Done
          </button>
        </div>
      )}
      {!wide && legend}
      {colourDraft && (() => {
        const d = colourDraft;
        const cat = categories.find(c => c.title === d.name);
        const preview = getSliceColor(d.name);
        const pct = Math.round(clampSliceOpacity(d.opacity) * 100);
        const isDefault = !d.hex && pct >= 100;
        return (
          <Modal onClose={closeColourEditor} blur={false} dim={0.5} animate={false}>
            <div style={{ background: "var(--c-bg-card)", borderRadius: 20, padding: "18px 18px 16px", width: "100%", maxWidth: 360, boxShadow: "0 12px 40px rgba(0,0,0,0.22)", fontFamily: "'Quicksand','Nunito',sans-serif" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", background: preview, flexShrink: 0, boxShadow: `0 0 0 3px ${preview}33` }} />
                <span style={{ flex: 1, fontSize: 16, fontWeight: 800, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                <button onClick={closeColourEditor} aria-label="Close" style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--c-text-soft)", padding: 4, display: "flex" }}><X size={18} /></button>
              </div>
              <ModalColorPicker alwaysOpen pickerFallback={sliceColor(d.name, categories.map(c => c.title === d.name ? { ...c, sliceHex: "", sliceOpacity: 1 } : c))} currentColor={d.hex} onColorChange={hex => setColourDraft(p => p && ({ ...p, hex }))} S={S} />
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--c-text-soft)" }}>Intensity</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: readableOnWhite(preview) }}>{pct}%</span>
                </div>
                <input type="range" min={Math.round(SLICE_OPACITY_MIN * 100)} max={100} step={5} value={pct}
                  onChange={e => setColourDraft(p => p && ({ ...p, opacity: Number(e.target.value) / 100 }))}
                  aria-label={`${d.name} colour intensity`}
                  style={{ width: "100%", accentColor: "var(--c-primary)", outline: "none" }} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button onClick={() => setColourDraft(p => p && ({ ...p, hex: "", opacity: 1 }))} disabled={isDefault}
                  style={{ flex: 1, padding: "10px 0", borderRadius: 12, border: "1.5px solid var(--c-border)", background: "var(--c-bg-card)", color: isDefault ? "var(--c-text-soft)" : "var(--c-text-mid)", fontSize: 13, fontWeight: 700, cursor: isDefault ? "default" : "pointer", fontFamily: "inherit" }}>
                  Reset
                </button>
                <button onClick={saveColourDraftWithPop} disabled={!cat || colourSaving} className="clarity-add-btn"
                  style={{ flex: 1, padding: "10px 0", borderRadius: 12, border: "none", background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "#fff", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  {colourSaving ? <><CheckCircle size={16} />Saved!</> : "Save"}
                </button>
              </div>
            </div>
          </Modal>
        );
      })()}
      {sessionDetailsFor && (() => {
        // The linked habit/task/to-do may no longer exist (deleted since it was
        // logged) — itemRef then comes back null and the modal falls back to a
        // trimmed-down view built from the session's own stored fields instead
        // of bailing out. A session with no kind/refId at all (nothing was
        // assigned when it was clocked in) behaves the same way, showing
        // "Uncategorized".
        const ref = (sessionDetailsFor.kind && sessionDetailsFor.refId != null)
          ? resolveItemRef(sessionDetailsFor.kind, sessionDetailsFor.refId)
          : null;
        return createPortal(
          <ItemDetailsModal
            kind={sessionDetailsFor.kind} itemRef={ref} categories={categories}
            focusSessions={focusSessions} habitLogs={habitLogs} targetDate={sessionDetailsFor.date} hideDuration={hideDuration}
            setTasks={setTasks} onClose={() => setSessionDetailsFor(null)}
            sessionToDelete={sessionDetailsFor}
            onDeleteSession={(s) => {
              setFocusSessions(p => withoutSession(p, s));
              // A Time habit's minutes for that day go back down with it (sessionEdit.js).
              if (setHabitLogs) setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, s, -(s.minutes || 0)));
            }}
            onEditSessionTimes={(s, start, end, estimated) => {
              const delta = minutesBetween(start, end) - (s.minutes || 0);
              setFocusSessions(p => withSessionTimes(p, s, start, end, estimated));
              if (setHabitLogs) setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, s, delta));
              setSessionDetailsFor(prev => prev && sameSession(prev, s) ? withSessionTimes([prev], prev, start, end, estimated)[0] : prev);
            }}
            onRemoveSession={(s) => {
              setFocusSessions(p => withoutSession(p, s));
              if (setHabitLogs) setHabitLogs(prev => habitLogsAfterSessionChange(prev, habits, s, -(s.minutes || 0)));
              // The day's last log for this item gone: nothing left to show.
              const left = focusSessions.filter(x => !sameSession(x, s) && x.date === sessionDetailsFor.date
                && (s.refId != null ? (x.refId === s.refId && x.kind === s.kind) : (!x.refId && (x.label || "").trim() === (s.label || "").trim())));
              if (!left.length) setSessionDetailsFor(null);
              else if (sameSession(sessionDetailsFor, s)) setSessionDetailsFor(left[0]);
            }}
            onSetSessionNote={(s, text) => {
              setFocusSessions(p => withSessionNote(p, s, text));
              // The panel's fallback (a session no grouping matched) reads this copy.
              setSessionDetailsFor(prev => prev && sameSession(prev, s) ? withSessionNote([prev], prev, text)[0] : prev);
            }}
            onRenameSession={(newLabel) => {
              const target = sessionDetailsFor;
              setFocusSessions(prev => prev.map(x => {
                const sameItem = target.refId != null
                  ? (x.kind === target.kind && x.refId === target.refId)
                  : (!x.refId && (x.label || "").trim() === (target.label || "").trim());
                return (sameItem && x.date === target.date) ? { ...x, label: newLabel } : x;
              }));
              setSessionDetailsFor(prev => prev ? { ...prev, label: newLabel } : prev);
            }}
          />, document.body);
      })()}
      {confirmBulkDelete && createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
          <div className="clarity-fade" style={{ position: "absolute", inset: 0, background: "rgba(40,20,35,0.5)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }} onClick={() => setConfirmBulkDelete(false)} />
          <div className="clarity-pop" style={{ position: "relative", background: "var(--c-bg-card)", borderRadius: 20, padding: "24px 22px 18px", maxWidth: 300, width: "100%", textAlign: "center", boxShadow: "0 10px 44px rgba(0,0,0,0.22)" }}>
            <ConfirmHeader title={`Are you sure you want to delete ${selectedCount === 1 ? "this item" : `these ${selectedCount} items`}?`} />
            <div style={CONFIRM_STACK}>
              <button onClick={() => setConfirmBulkDelete(false)} style={CONFIRM_CANCEL}>Cancel</button>
              <button onClick={performBulkDelete} style={CONFIRM_DANGER}>Delete</button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
